import * as THREE from 'three';
import { COLORS_CONST } from '../../helpers/styles';
import { DrawingSubstrateType, NamePointType, PruningObjectType } from '../types';
import { Edit } from '../work/Edit';
import BasicObject from './BasicObject';

interface PropsConstructor {
  p0: THREE.Vector3;
  p1: THREE.Vector3;
  p2: THREE.Vector3;
  p3: THREE.Vector3;
}
export class PruningObject extends THREE.Group implements PruningObjectType {
  private selectPoint: {
    pOrigin: NamePointType;
    p1: NamePointType;
    direct1: 'x' | 'z';
    p2: NamePointType;
    direct2: 'x' | 'z';
    limitX: number;
    limitZ: number;
  } | null = null;
  private line: THREE.Line | null = null;
  private maxLimit = {
    xMin: 0,
    xMax: 0,
    zMin: 0,
    zMax: 0,
  };

  cropParams = {
    offsetX: 0,
    offsetZ: 0,
    offsetWidth: 0,
    offsetHeight: 0,
    cropX: 0,
    cropY: 0,
    lengthX: 0,
    lengthY: 0,
    widthCroup: 0,
    heightCroup: 0,
  };
  point0: THREE.Vector3;
  point1: THREE.Vector3;
  point2: THREE.Vector3;
  point3: THREE.Vector3;
  points: Array<THREE.Vector3>;
  constructor({ p0, p1, p2, p3 }: PropsConstructor) {
    super();
    this.point0 = p0.setY(0.01);
    this.point1 = p1.setY(0.01);
    this.point2 = p2.setY(0.01);
    this.point3 = p3.setY(0.01);
    this.points = [this.point0, this.point1, this.point2, this.point3];
    this.maxLimit.xMin = p0.x;
    this.maxLimit.xMax = p2.x;
    this.maxLimit.zMin = p2.z;
    this.maxLimit.zMax = p3.z;

    this.buildGroup();
  }

  buildGroup() {
    this.addCircles();
    this.addLines();
  }
  addCircles() {
    this.points.forEach((item, index) => {
      const mesh = new CirclesObject(`point${index}` as NamePointType);
      if (Edit.dragObject) {
        Edit.dragObject.addObjectMove(mesh);
      }
      this.add(mesh);
      mesh.position.copy(item);
    });
  }
  addLines() {
    const points = [...this.points];
    const geometry = new THREE.Geometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 3 });
    const line = new THREE.LineLoop(geometry, material);
    line.name = 'line_object';
    this.line = line;
    this.add(line);
  }

  dragPointStart(namePoint: NamePointType) {
    switch (namePoint) {
      case 'point0':
        this.selectPoint = {
          pOrigin: namePoint,
          p1: 'point1',
          direct1: 'x',
          p2: 'point3',
          direct2: 'z',
          limitX: this.maxLimit.xMin,
          limitZ: this.maxLimit.zMax,
        };
        break;
      case 'point1':
        this.selectPoint = {
          pOrigin: namePoint,
          p1: 'point0',
          direct1: 'x',
          p2: 'point2',
          direct2: 'z',
          limitX: this.maxLimit.xMin,
          limitZ: this.maxLimit.zMin,
        };
        break;
      case 'point2':
        this.selectPoint = {
          pOrigin: namePoint,
          p1: 'point3',
          direct1: 'x',
          p2: 'point1',
          direct2: 'z',
          limitX: this.maxLimit.xMax,
          limitZ: this.maxLimit.zMin,
        };
        break;
      case 'point3':
        this.selectPoint = {
          pOrigin: namePoint,
          p1: 'point2',
          direct1: 'x',
          p2: 'point0',
          direct2: 'z',
          limitX: this.maxLimit.xMax,
          limitZ: this.maxLimit.zMax,
        };
        break;
    }
  }
  dragPoint(position: THREE.Vector3) {
    // const position = _position.clone();
    if (this.selectPoint) {
      const limOldX = this[this.selectPoint.pOrigin].x;
      const limOldZ = this[this.selectPoint.pOrigin].z;
      const p1 = this[this.selectPoint.p1].clone();
      const p2 = this[this.selectPoint.p2].clone();

      const limitX = position.x >= this.maxLimit.xMin && position.x <= this.maxLimit.xMax;
      const limitZ = position.z >= this.maxLimit.zMin && position.z <= this.maxLimit.zMax;

      if (position.distanceTo(p1) < 0.3) {
        position.setZ(limOldZ);
      }
      if (position.distanceTo(p2) < 0.3) {
        position.setX(limOldX);
        if (position.distanceTo(p1) < 0.3) {
          position.setZ(limOldZ);
        }
      }

      if (!limitX) {
        position.setX(this.selectPoint.limitX);
      }
      if (!limitZ) {
        position.setZ(this.selectPoint.limitZ);
      }

      this[this.selectPoint.pOrigin].copy(position);
      p1[this.selectPoint.direct1] = position[this.selectPoint.direct1];
      p2[this.selectPoint.direct2] = position[this.selectPoint.direct2];
      this[this.selectPoint.p1].copy(p1);
      this[this.selectPoint.p2].copy(p2);

      this.children.forEach(item => {
        if (item instanceof CirclesObject) {
          item.position.copy(this[item.namePoint]);
          const index = +item.namePoint.replace('point', '');
          this.updateLines(index, this[item.namePoint]);
        }
      });
    }
  }
  dragPointEnd() {
    const parent = this.parent as DrawingSubstrateType;
    const { imgOriginWidth, offsetX, imgOriginHeight, offsetZ, lengthX, lengthY } =
      parent.getCropParams();

    this.cropParams.cropX =
      Math.abs(-imgOriginWidth / 2 - (this.point0.x + offsetX)) / imgOriginWidth;
    this.cropParams.cropY =
      Math.abs(imgOriginHeight / 2 - (this.point0.z + offsetZ)) / imgOriginHeight;

    const startWidth = new THREE.Vector3(-imgOriginWidth / 2, 0, this.point2.z);
    this.cropParams.lengthX = (startWidth.distanceTo(this.point2) + lengthX) / imgOriginWidth;

    const startHeight = new THREE.Vector3(this.point2.x, 0, imgOriginHeight / 2);
    this.cropParams.lengthY = (startHeight.distanceTo(this.point2) - lengthY) / imgOriginHeight;

    this.cropParams.widthCroup = this.point1.distanceTo(this.point2);
    this.cropParams.heightCroup = this.point1.distanceTo(this.point0);

    const distX = this.cropParams.widthCroup / 2;
    const distZ = this.cropParams.heightCroup / 2;

    const middleX = this.point1.clone().add(new THREE.Vector3(distX, 0, 0));
    const middleZ = this.point1.clone().add(new THREE.Vector3(0, 0, distZ));
    const midLenX = this.point2.clone().add(new THREE.Vector3(-distX, 0, 0));
    const midLenZ = this.point2.clone().add(new THREE.Vector3(0, 0, distZ));

    this.cropParams.offsetX = middleX.x;
    this.cropParams.offsetZ = middleZ.z;
    this.cropParams.offsetWidth = midLenX.x;
    this.cropParams.offsetHeight = midLenZ.z;

    this.selectPoint = null;
  }
  removePruningObj() {
    while (this.children.length > 0) {
      const child = this.children[0];
      if (child instanceof THREE.LineLoop) {
        child.geometry.dispose();
        this.remove(child);
      } else if (child instanceof CirclesObject) {
        if (Edit.dragObject) {
          Edit.dragObject.removeObjectMove(child.uuid);
        }
        child.removeMesh();
      }
    }
    this.parent?.remove(this);
  }

  private updateLines(index: number, newPos: THREE.Vector3) {
    if (this.line) {
      const geomLine = this.line.geometry as THREE.Geometry;
      geomLine.vertices[index].copy(newPos);
      geomLine.verticesNeedUpdate = true;
    }
  }
}

class CirclesObject extends BasicObject {
  namePoint: NamePointType;
  constructor(namePoint: NamePointType) {
    super();
    this.name = 'circle_object';
    this.namePoint = namePoint;

    this.buildGeometry();
  }
  buildGeometry() {
    super.buildGeometry();
    this.geometry = new THREE.CircleGeometry(0.15, 32);
    this.geometry.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
    this.material = new THREE.MeshBasicMaterial({
      color: COLORS_CONST.black,
    });
  }
  dragStart() {
    const parent = this.parent as PruningObjectType;
    parent.dragPointStart(this.namePoint);
  }

  drag() {
    const parent = this.parent as PruningObjectType;
    parent.dragPoint(this.position);
  }
  dragEnd() {
    const parent = this.parent as PruningObjectType;
    parent.dragPointEnd();
  }
  removeMesh() {
    super.removeMesh(this);
  }
}
